import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, Search, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth, usePermissions } from '../hooks/useAuth'
import api, { leadApi, type Lead, type PaginatedResponse } from '../lib/api'
import { formatStage } from '../lib/format'
import { MERCHANT_NAV_ROLES } from '../lib/navAccess'
import type { Merchant } from '../pages/merchants/useMerchantData'

/** Same roles as the Merchants nav. GET /api/merchants enforces this list. */
export const MERCHANT_SEARCH_ROLES = MERCHANT_NAV_ROLES

export function leadsVisibleInSearch<T extends {
  assigned_to?: string | null
  assigned_to_secondary?: string | null
}>(
  leads: T[],
  user: { id: string; role: string } | null | undefined,
): T[] {
  if (user?.role !== 'sales_rep') return leads
  return leads.filter((lead) => (
    lead.assigned_to === user.id || lead.assigned_to_secondary === user.id
  ))
}

interface SearchResult {
  id: string
  href: string
  title: string
  meta: string
  kind: 'lead' | 'merchant'
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

function leadTitle(lead: Lead): string {
  return lead.dba_name || lead.legal_name || 'Unnamed lead'
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasRole } = usePermissions()
  const canSearchMerchants = hasRole([...MERCHANT_SEARCH_ROLES])
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!open) {
      setQuery('')
      setDebounced('')
      setActiveIndex(0)
      return
    }
    inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 200)
    return () => window.clearTimeout(timer)
  }, [query])

  const ready = open && debounced.length >= 2

  const leadsQuery = useQuery({
    queryKey: ['command-search', 'leads', debounced],
    queryFn: () => leadApi.list({ search: debounced, per_page: 6 }),
    enabled: ready,
  })

  const merchantsQuery = useQuery({
    queryKey: ['command-search', 'merchants', debounced],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<Merchant>>('/merchants', {
        params: { search: debounced, per_page: 6 },
      })
      return response.data
    },
    enabled: ready && canSearchMerchants,
  })

  const results = useMemo<SearchResult[]>(() => {
    const leads = leadsVisibleInSearch(
      Array.isArray(leadsQuery.data?.data) ? leadsQuery.data.data : [],
      user,
    )
    const merchants = canSearchMerchants && Array.isArray(merchantsQuery.data?.data)
      ? merchantsQuery.data.data
      : []

    return [
      ...leads.map((lead) => ({
        id: `lead-${lead.id}`,
        href: `/leads/${lead.id}`,
        title: leadTitle(lead),
        meta: formatStage(lead.stage) || 'Lead',
        kind: 'lead' as const,
      })),
      ...merchants.map((merchant) => ({
        id: `merchant-${merchant.id}`,
        href: `/merchants/${merchant.id}`,
        title: merchant.dba_name || 'Unnamed merchant',
        meta: merchant.status ? merchant.status.replace(/_/g, ' ') : 'Merchant',
        kind: 'merchant' as const,
      })),
    ]
  }, [canSearchMerchants, leadsQuery.data, merchantsQuery.data, user])

  useEffect(() => {
    setActiveIndex(0)
  }, [debounced, results.length])

  if (!open) return null

  const searching = ready && (leadsQuery.isFetching || merchantsQuery.isFetching)
  const failed = ready && (leadsQuery.isError || (canSearchMerchants && merchantsQuery.isError))

  const go = (href: string) => {
    onClose()
    navigate(href)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (results.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % results.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (index - 1 + results.length) % results.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const selected = results[activeIndex]
      if (selected) go(selected.href)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Close search"
        className="absolute inset-0 bg-brand-950/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search leads and merchants"
        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-white shadow-pop"
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="h-4 w-4 shrink-0 text-muted" />
          <input
            ref={inputRef}
            role="searchbox"
            aria-label="Search leads and merchants"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search leads and merchants"
            className="h-12 w-full border-0 bg-transparent text-sm text-ink outline-none placeholder:text-slate-400 focus:ring-0"
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {!ready ? (
            <p className="px-3 py-6 text-sm text-muted">Type at least 2 characters.</p>
          ) : failed ? (
            <p className="px-3 py-6 text-sm text-rose-700">Search is unavailable.</p>
          ) : searching && results.length === 0 ? (
            <p className="px-3 py-6 text-sm text-muted">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-6 text-sm text-muted">No matching leads or merchants.</p>
          ) : (
            <ul aria-label="Search results">
              {results.map((result, index) => {
                const Icon = result.kind === 'lead' ? Users : Building2
                const active = index === activeIndex
                return (
                  <li key={result.id}>
                    <button
                      type="button"
                      onClick={() => go(result.href)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left ${
                        active ? 'bg-brand-50 text-brand-900' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-brand-600" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{result.title}</span>
                        <span className="block truncate text-xs capitalize text-muted">{result.meta}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
